// ==============================================
// Shared Navigation Libraries — the third shelf of the Navigation Library
// page. Explore listed libraries, request access (auto-approved when the
// curator allows it), browse a library you're in, copy items into My library,
// and — as a curator — create libraries, publish items, decide requests.
// Renders inside the page's left column; previews go to the page's map via
// onPreview so a library item is looked at exactly like your own.
// Readability mandate: nothing under 14px (text-sm), primary copy 16px+.
// ==============================================

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  GlobeAltIcon, LockClosedIcon, LockOpenIcon, UserGroupIcon, CheckIcon, XMarkIcon, ArrowLeftIcon,
  PlusIcon, PencilIcon, TrashIcon, BookmarkIcon, ArrowPathIcon, BoltIcon, EyeSlashIcon, StarIcon as StarOutline, FlagIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import type { NavElement, NavType } from '../../types/nav';
import type { ExploreLibrary, NavLibrary, LibraryItem, LibraryInput, LibraryMember, ExploreSort, RatingTag, LibraryDetail, ReportReason } from '../../types/navLibraries';
import { RATING_TAGS, RATING_TAG_LABEL, REPORT_REASONS, REPORT_REASON_LABEL } from '../../types/navLibraries';

const TYPE_COLOR: Record<NavType, string> = { route: '#3b82f6', track: '#22c55e', waypoint: '#eab308' };

const errMsg = (err: unknown, fallback: string) => {
  if (isAxiosError(err)) { const m = (err.response?.data as { error?: string } | undefined)?.error; if (m) return m; }
  return fallback;
};
const fmtAgo = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 86_400_000;
  if (d < 1) return 'today'; if (d < 2) return 'yesterday'; if (d < 30) return `${Math.floor(d)} d ago`;
  return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
};
const itemMeta = (it: LibraryItem) => {
  const parts: string[] = [];
  if (it.stats.distance_nm) parts.push(`${it.stats.distance_nm.toFixed(1)} nm`);
  if (it.type === 'route') parts.push(`${it.stats.point_count} waypoints`);
  if (it.type === 'track') parts.push(`${it.stats.point_count} points`);
  if (it.updatedAt) parts.push(`updated ${fmtAgo(it.updatedAt)}`);
  return parts.join(' · ');
};
/** A library item viewed as an element, so the page's map can draw it. */
const asElement = (it: LibraryItem, lib: NavLibrary): NavElement => ({
  id: it.elementId, userId: lib.ownerUserId, type: it.type, name: it.name, description: it.description, tags: it.tags,
  points: it.points, stats: it.stats, origin: 'library', sourceDeviceId: null, sharedByUserId: lib.ownerUserId,
  sharedByName: lib.ownerName, createdAt: it.addedAt, updatedAt: it.updatedAt,
});

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-40';
const btnPrimary = `${btn} bg-amber-400 text-slate-900 hover:brightness-105`;
const btnGhost = `${btn} border border-slate-600 text-white hover:border-amber-400`;
const btnDanger = `${btn} border border-red-400/60 text-red-300 hover:bg-red-900/30`;
const input = 'w-full rounded-lg bg-slate-900 border border-slate-600 text-white text-base px-3 py-2 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/70';

function AccessChips({ lib }: { lib: NavLibrary }) {
  return (
    <span className="inline-flex gap-1.5 flex-wrap">
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 border border-slate-600 rounded-full px-2 py-0.5">
        {lib.accessMode === 'open' ? <><BoltIcon className="w-3.5 h-3.5 text-emerald-300" /> Auto-approve</> : <><LockClosedIcon className="w-3.5 h-3.5" /> Owner approves</>}
      </span>
      {lib.visibility === 'unlisted' && (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 border border-slate-600 rounded-full px-2 py-0.5"><EyeSlashIcon className="w-3.5 h-3.5" /> Unlisted</span>
      )}
      {lib.takenDownAt && <span className="inline-flex items-center gap-1 text-[13px] font-semibold text-red-200 border border-red-700 bg-red-900/30 rounded-full px-2 py-0.5"><FlagIcon className="w-3.5 h-3.5" /> Taken down</span>}
    </span>
  );
}

/** Five stars; interactive when onChange is given. */
function Stars({ value, onChange, size = 'w-4 h-4' }: { value: number; onChange?: (v: number) => void; size?: string }) {
  return (
    <span className="inline-flex items-center" role={onChange ? 'radiogroup' : undefined} aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= Math.round(value);
        const Icon = on ? StarSolid : StarOutline;
        return onChange ? (
          <button key={n} type="button" role="radio" aria-checked={n === value} aria-label={`${n} star${n === 1 ? '' : 's'}`} onClick={() => onChange(n)} className="p-0.5">
            <Icon className={`${size} ${on ? 'text-amber-400' : 'text-slate-500'}`} />
          </button>
        ) : <Icon key={n} className={`${size} ${on ? 'text-amber-400' : 'text-slate-600'}`} />;
      })}
    </span>
  );
}

/** "★★★★☆ 4.6 (57)" or "No ratings yet" — same on cards and detail. */
function RatingLine({ avg, count }: { avg: number | null; count: number }) {
  if (!count || avg === null) return <span className="text-sm text-slate-500">No ratings yet</span>;
  return <span className="inline-flex items-center gap-1.5 text-sm text-slate-300 tabular-nums"><Stars value={avg} /> <b className="text-white">{avg.toFixed(1)}</b> ({count})</span>;
}

// ---------------- create / edit ----------------

export function LibraryFormModal({ initial, onClose, onSaved }: { initial: NavLibrary | null; onClose: () => void; onSaved: (lib: NavLibrary) => void }) {
  const [form, setForm] = useState<LibraryInput>({
    name: initial?.name ?? '', description: initial?.description ?? '', region: initial?.region ?? '',
    visibility: initial?.visibility ?? 'listed', access_mode: initial?.accessMode ?? 'request',
  });
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => initial ? api.navLibUpdate(initial.id, form) : api.navLibCreate(form),
    onSuccess: onSaved,
    onError: (e) => setError(errMsg(e, 'Could not save the library.')),
  });
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="lib-form-title">
      <form className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-lg space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <h3 id="lib-form-title" className="text-lg font-bold text-white">{initial ? 'Edit shared library' : 'New shared library'}</h3>
        <label className="block text-sm font-semibold text-slate-200">Name
          <input className={`${input} mt-1`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Fiji Lau Group – reef passes" maxLength={120} autoFocus />
        </label>
        <label className="block text-sm font-semibold text-slate-200">Region <span className="text-slate-400 font-normal">(optional)</span>
          <input className={`${input} mt-1`} value={form.region ?? ''} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. Fiji, Hauraki Gulf" maxLength={120} />
        </label>
        <label className="block text-sm font-semibold text-slate-200">Description <span className="text-slate-400 font-normal">(optional)</span>
          <textarea className={`${input} mt-1`} rows={3} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's in it, when you sailed it, anything skippers should know." maxLength={2000} />
        </label>
        {initial?.takenDownAt ? <p className="text-sm text-red-200 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">Taken down by Vortex Marine — who can find or join this library can't be changed. Contact support@vortexmarine.com.</p> : (
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="flex items-start gap-3 bg-slate-900/60 border border-slate-700 rounded-xl p-3 cursor-pointer">
            <input type="checkbox" className="mt-1 w-5 h-5 rounded bg-slate-700 border-slate-600 text-amber-400 focus:ring-amber-400"
              checked={form.access_mode === 'open'} onChange={(e) => setForm({ ...form, access_mode: e.target.checked ? 'open' : 'request' })} />
            <span><span className="block text-sm font-semibold text-white">Automatically approve access requests</span>
              <span className="block text-sm text-slate-400">Anyone with the Navigation Library can join without waiting for you.</span></span>
          </label>
          <label className="flex items-start gap-3 bg-slate-900/60 border border-slate-700 rounded-xl p-3 cursor-pointer">
            <input type="checkbox" className="mt-1 w-5 h-5 rounded bg-slate-700 border-slate-600 text-amber-400 focus:ring-amber-400"
              checked={form.visibility === 'listed'} onChange={(e) => setForm({ ...form, visibility: e.target.checked ? 'listed' : 'unlisted' })} />
            <span><span className="block text-sm font-semibold text-white">Show in Explore</span>
              <span className="block text-sm text-slate-400">Off = only people you send the link to can find it.</span></span>
          </label>
        </div>)}
        {error && <p role="alert" className="text-sm font-medium text-red-300 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="submit" disabled={save.isPending || !form.name.trim()} className={btnPrimary}>{save.isPending ? 'Saving…' : initial ? 'Save' : 'Create library'}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------- add-to-library (from My library) ----------------

export function AddToLibraryModal({ elements, onClose }: { elements: NavElement[]; onClose: () => void }) {
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const { data: mine, isLoading } = useQuery({ queryKey: ['nav-lib-mine'], queryFn: () => api.navLibMine() });
  const add = useMutation({
    mutationFn: (libId: string) => api.navLibAddItems(libId, elements.map((e) => e.id)),
    onSuccess: (r, libId) => {
      queryClient.invalidateQueries({ queryKey: ['nav-lib'] });
      queryClient.invalidateQueries({ queryKey: ['nav-lib-mine'] });
      queryClient.invalidateQueries({ queryKey: ['nav-lib-detail', libId] });
      queryClient.invalidateQueries({ queryKey: ['nav-lib-published-in'] });
      addToast({ type: 'success', title: r.added ? `Published ${r.added} item${r.added === 1 ? '' : 's'}` : 'Already published', message: r.skipped ? `${r.skipped} already in that library` : undefined });
      onClose();
    },
    onError: (e) => addToast({ type: 'error', title: 'Could not publish', message: errMsg(e, 'Please try again.') }),
  });
  if (creating) {
    return <LibraryFormModal initial={null} onClose={() => setCreating(false)} onSaved={(lib) => { queryClient.invalidateQueries({ queryKey: ['nav-lib-mine'] }); add.mutate(lib.id); }} />;
  }
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="add-lib-title">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
        <h3 id="add-lib-title" className="text-lg font-bold text-white mb-1">Add to a shared library</h3>
        <p className="text-sm text-slate-400 mb-4">{elements.length === 1 ? `“${elements[0].name}”` : `${elements.length} items`} will be visible to everyone you've given access. Edits you make later flow through automatically.</p>
        {isLoading ? <div className="text-slate-400 text-sm py-3">Loading…</div> : (
          <ul className="space-y-1.5 max-h-64 overflow-y-auto mb-3">
            {(mine ?? []).filter((l) => !l.takenDownAt).map((l) => (
              <li key={l.id}>
                <button type="button" disabled={add.isPending} onClick={() => add.mutate(l.id)}
                  className="w-full text-left rounded-lg border border-slate-700 hover:border-amber-400 px-3 py-2 disabled:opacity-50">
                  <div className="text-base font-semibold text-white">{l.name}</div>
                  <div className="text-sm text-slate-400">{l.itemCount} items · {l.memberCount} members</div>
                </button>
              </li>
            ))}
            {mine && mine.length === 0 && <li className="text-sm text-slate-400 py-2">You haven't created a shared library yet.</li>}
          </ul>
        )}
        <div className="flex justify-between gap-2">
          <button type="button" onClick={() => setCreating(true)} className={btnGhost}><PlusIcon className="w-4 h-4" /> New shared library</button>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ---------------- the panel ----------------

type View = { kind: 'explore' } | { kind: 'mine' } | { kind: 'subscribed' } | { kind: 'detail'; id: string } | { kind: 'curator'; userId: string; back: View };

export function SharedLibrariesPanel({ onPreview, initialLibraryId }: { onPreview: (elements: NavElement[]) => void; initialLibraryId?: string | null }) {
  const [view, setView] = useState<View>(initialLibraryId ? { kind: 'detail', id: initialLibraryId } : { kind: 'explore' });
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<ExploreSort>('top');
  const [form, setForm] = useState<{ open: boolean; initial: NavLibrary | null }>({ open: false, initial: null });
  const queryClient = useQueryClient();

  const { data: explore, isLoading: exploreLoading } = useQuery({ queryKey: ['nav-lib', 'explore', q, sort], queryFn: () => api.navLibExplore(q || undefined, sort), enabled: view.kind === 'explore' });
  const { data: mine } = useQuery({ queryKey: ['nav-lib-mine'], queryFn: () => api.navLibMine(), enabled: view.kind === 'mine' });
  const { data: subscribed } = useQuery({ queryKey: ['nav-lib', 'subscribed'], queryFn: () => api.navLibSubscribed(), enabled: view.kind === 'subscribed' });

  useEffect(() => { if (view.kind !== 'detail') onPreview([]); }, [view.kind, onPreview]);
  const openCurator = (userId: string) => setView((v) => ({ kind: 'curator', userId, back: v.kind === 'curator' ? v.back : v }));

  const pending = (mine ?? []).reduce((a, l) => a + l.pendingCount, 0);

  const tabs = (
    <div className="px-3 pt-3 flex items-center gap-1 flex-wrap">
      {([['explore', 'Explore'], ['subscribed', 'Joined'], ['mine', 'My shared libraries']] as const).map(([k, label]) => (
        <button key={k} type="button" onClick={() => setView({ kind: k })} aria-pressed={view.kind === k}
          className={`px-3 py-1.5 rounded-md text-sm font-semibold ${view.kind === k ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
          {label}{k === 'mine' && pending > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-amber-400 text-slate-900 text-xs font-bold">{pending}</span>}
        </button>
      ))}
      <span className="flex-1" />
      <button type="button" onClick={() => setForm({ open: true, initial: null })} className={btnPrimary}><PlusIcon className="w-4 h-4" /> New</button>
    </div>
  );

  const card = (l: ExploreLibrary | NavLibrary, extra?: React.ReactNode) => (
    <button key={l.id} type="button" onClick={() => setView({ kind: 'detail', id: l.id })}
      className="w-full text-left rounded-xl border border-slate-700 hover:border-amber-400 bg-slate-800/60 px-4 py-3">
      <div className="flex items-start gap-2">
        <GlobeAltIcon className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-base font-bold text-white truncate">{l.name}</div>
          <div className="text-sm text-slate-300">by {l.ownerName ?? 'a Scout user'}{l.region ? ` · ${l.region}` : ''}</div>
          {l.description && <div className="text-sm text-slate-400 mt-1 line-clamp-2">{l.description}</div>}
          <div className="mt-1.5"><RatingLine avg={l.ratingAvg} count={l.ratingCount} /></div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-400 mt-1.5 tabular-nums">
            <span>{l.itemCount} item{l.itemCount === 1 ? '' : 's'}</span>
            {l.copyCount > 0 && <span>copied {l.copyCount}×</span>}
            <span className="inline-flex items-center gap-1"><UserGroupIcon className="w-4 h-4" />{l.memberCount}</span>
            <span>updated {fmtAgo(l.updatedAt)}</span>
            <AccessChips lib={l} />
            {extra}
          </div>
        </div>
      </div>
    </button>
  );

  const membershipPill = (m: ExploreLibrary['membership'], mineFlag: boolean) => {
    if (mineFlag) return <span className="text-xs font-bold uppercase tracking-wide text-amber-400 border border-amber-400/50 rounded px-1.5 py-0.5">Yours</span>;
    if (m === 'approved') return <span className="text-xs font-bold uppercase tracking-wide text-emerald-300 border border-emerald-400/50 rounded px-1.5 py-0.5">Joined</span>;
    if (m === 'requested') return <span className="text-xs font-bold uppercase tracking-wide text-slate-300 border border-slate-500 rounded px-1.5 py-0.5">Requested</span>;
    return null;
  };

  return (
    <div className="flex flex-col h-full">
      {view.kind !== 'detail' && view.kind !== 'curator' && tabs}
      <div className="overflow-y-auto flex-1 p-3 space-y-2">
        {view.kind === 'explore' && (<>
          <div className="flex gap-2">
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search libraries by name or region…" className={input} />
            <select aria-label="Sort libraries" value={sort} onChange={(e) => setSort(e.target.value as ExploreSort)} className="rounded-lg bg-slate-900 border border-slate-600 text-white text-sm px-2 py-2 shrink-0">
              <option value="top">Top rated</option>
              <option value="popular">Most copied</option>
              <option value="new">Recently updated</option>
            </select>
          </div>
          {exploreLoading ? <div className="text-slate-400 text-sm py-4">Loading…</div>
            : (explore ?? []).length === 0 ? <div className="text-slate-400 text-base py-6 text-center">No shared libraries yet. Be the first — publish some of your routes.</div>
            : explore!.map((l) => card(l, membershipPill(l.membership, l.mine)))}
        </>)}
        {view.kind === 'subscribed' && (
          (subscribed ?? []).length === 0 ? <div className="text-slate-400 text-base py-6 text-center">You haven't joined any libraries. Find one in Explore.</div>
            : subscribed!.map((l) => card(l, membershipPill(l.membership, false)))
        )}
        {view.kind === 'mine' && (
          (mine ?? []).length === 0 ? <div className="text-slate-400 text-base py-6 text-center">Publish your first library: select items in My library and choose “Add to shared library”, or press New.</div>
            : mine!.map((l) => card(l, l.pendingCount > 0 ? <span className="text-xs font-bold text-amber-400">{l.pendingCount} request{l.pendingCount === 1 ? '' : 's'} waiting</span> : null))
        )}
        {view.kind === 'detail' && (
          <LibraryDetailView id={view.id} onBack={() => setView({ kind: 'explore' })} onPreview={onPreview} onEdit={(lib) => setForm({ open: true, initial: lib })} onCurator={openCurator} />
        )}
        {view.kind === 'curator' && (
          <CuratorView userId={view.userId} onBack={() => setView(view.back)} onOpen={(id) => setView({ kind: 'detail', id })} />
        )}
      </div>
      {form.open && (
        <LibraryFormModal initial={form.initial} onClose={() => setForm({ open: false, initial: null })}
          onSaved={(lib) => { setForm({ open: false, initial: null }); queryClient.invalidateQueries({ queryKey: ['nav-lib'] }); queryClient.invalidateQueries({ queryKey: ['nav-lib-mine'] }); queryClient.invalidateQueries({ queryKey: ['nav-lib-detail', lib.id] }); setView({ kind: 'detail', id: lib.id }); }} />
      )}
    </div>
  );
}

// ---------------- detail ----------------

function LibraryDetailView({ id, onBack, onPreview, onEdit, onCurator }: { id: string; onBack: () => void; onPreview: (els: NavElement[]) => void; onEdit: (lib: NavLibrary) => void; onCurator: (userId: string) => void }) {
  const addToast = useToastStore((s) => s.addToast);
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showMembers, setShowMembers] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['nav-lib-detail', id], queryFn: () => api.navLibDetail(id) });
  const lib = data?.library;
  const isOwner = data?.role === 'owner';
  const canBrowse = data?.canBrowse === true;

  const refresh = () => { queryClient.invalidateQueries({ queryKey: ['nav-lib-detail', id] }); queryClient.invalidateQueries({ queryKey: ['nav-lib'] }); queryClient.invalidateQueries({ queryKey: ['nav-lib-mine'] }); };

  const selectedItems = useMemo(() => (data?.items ?? []).filter((i) => selected.has(i.elementId)), [data, selected]);
  useEffect(() => { if (lib && canBrowse) onPreview(selectedItems.map((i) => asElement(i, lib))); }, [selectedItems, lib, canBrowse, onPreview]);

  const request = useMutation({
    mutationFn: () => api.navLibRequest(id, message.trim() || undefined),
    onSuccess: (r) => { refresh(); addToast({ type: 'success', title: r.status === 'approved' ? 'You’re in' : 'Request sent', message: r.status === 'approved' ? 'This library approves automatically — browse away.' : 'The owner will be asked to approve you.' }); },
    onError: (e) => setError(errMsg(e, 'Could not request access.')),
  });
  const leave = useMutation({ mutationFn: () => api.navLibLeave(id), onSuccess: () => { refresh(); setSelected(new Set()); } });
  const copy = useMutation({
    mutationFn: (ids: string[]) => api.navLibCopy(id, ids),
    onSuccess: (r) => { refresh(); queryClient.invalidateQueries({ queryKey: ['nav-list'] }); setSelected(new Set()); addToast({ type: 'success', title: `Copied ${r.copied} item${r.copied === 1 ? '' : 's'} to My library`, message: 'Open My library to send them to your Scout.' }); },
    onError: (e) => setError(errMsg(e, 'Could not copy those items.')),
  });
  const removeItem = useMutation({ mutationFn: (elementId: string) => api.navLibRemoveItem(id, elementId), onSuccess: refresh });
  const del = useMutation({ mutationFn: () => api.navLibDelete(id), onSuccess: () => { addToast({ type: 'success', title: 'Library deleted' }); refresh(); onBack(); } });

  if (isLoading || !data || !lib) return <div className="text-slate-400 text-sm py-4">Loading…</div>;

  const toggle = (eid: string) => setSelected((s) => { const t = new Set(s); t.has(eid) ? t.delete(eid) : t.add(eid); return t; });

  return (
    <div className="space-y-3">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-amber-300 hover:underline"><ArrowLeftIcon className="w-4 h-4" /> All libraries</button>
      <div>
        <h2 className="text-lg font-bold text-white leading-tight">{lib.name}</h2>
        <div className="text-sm text-slate-300 mt-0.5">
          by <button type="button" onClick={() => onCurator(lib.ownerUserId)} className="text-amber-300 hover:underline font-semibold">{lib.ownerName ?? 'a Scout user'}</button>
          {lib.region ? ` · ${lib.region}` : ''} · {lib.itemCount} items · {lib.memberCount} members{lib.copyCount > 0 ? ` · copied ${lib.copyCount}×` : ''}
        </div>
        <div className="mt-1"><RatingLine avg={data.ratings.avg} count={data.ratings.count} /></div>
        {lib.description && <p className="text-sm text-slate-300 mt-2 whitespace-pre-line">{lib.description}</p>}
        <div className="mt-2"><AccessChips lib={lib} /></div>
      </div>

      {isOwner && lib.takenDownAt && (
        <div role="status" className="bg-red-900/30 border border-red-800/60 rounded-xl p-3 space-y-1">
          <p className="text-base font-bold text-red-200">Taken down by Vortex Marine</p>
          <p className="text-sm text-slate-200">This library is hidden from Explore; nobody can join it, see its items or copy from it, and it can't be listed again from here. Copies people already made stay theirs, marked as taken down. Nothing has been deleted. To discuss it, contact <a href="mailto:support@vortexmarine.com" className="text-amber-300 underline">support@vortexmarine.com</a>.</p>
        </div>
      )}

      {/* role CTA */}
      {isOwner ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => onEdit(lib)} className={btnGhost}><PencilIcon className="w-4 h-4" /> Edit</button>
          <button type="button" onClick={() => setShowMembers((v) => !v)} className={btnGhost}>
            <UserGroupIcon className="w-4 h-4" /> Requests & members{lib.pendingCount > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-amber-400 text-slate-900 text-xs font-bold">{lib.pendingCount}</span>}
          </button>
          <button type="button" onClick={() => setConfirmDelete(true)} className={btnDanger}><TrashIcon className="w-4 h-4" /> Delete</button>
        </div>
      ) : data.role === 'member' ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-300"><CheckIcon className="w-4 h-4" /> You have access</span>
          <button type="button" onClick={() => leave.mutate()} className={`${btnGhost} ml-auto`}>Leave</button>
        </div>
      ) : data.role === 'requested' ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-300">
          <span className="font-semibold">Request sent — waiting for {lib.ownerName ?? 'the owner'} to approve.</span>
          <button type="button" onClick={() => leave.mutate()} className={`${btnGhost} ml-auto`}>Withdraw</button>
        </div>
      ) : lib.accessMode === 'open' ? (
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-300 flex-1">You'll be approved automatically — join to browse and copy its items.</span>
          <button type="button" onClick={() => request.mutate()} disabled={request.isPending} className={btnPrimary}><LockOpenIcon className="w-4 h-4" /> Join</button>
        </div>
      ) : (
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 space-y-2">
          <p className="text-sm text-slate-300">{data.role === 'declined' ? 'Your earlier request was declined. You can ask again.' : data.role === 'revoked' ? 'Your access was removed. You can ask again.' : 'Ask the owner for access to see and copy the contents.'}</p>
          <input className={input} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Optional note to the owner — e.g. heading to Fiji in May" maxLength={500} />
          <button type="button" onClick={() => request.mutate()} disabled={request.isPending} className={btnPrimary}><LockClosedIcon className="w-4 h-4" /> Request access</button>
        </div>
      )}
      {error && <p role="alert" className="text-sm font-medium text-red-300 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">{error}</p>}

      {isOwner && showMembers && <MembersPanel id={id} onChanged={refresh} />}

      {/* bulk copy */}
      {canBrowse && !isOwner && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">{selected.size ? `${selected.size} selected` : 'Tick items to preview and copy'}</span>
          <button type="button" disabled={!selected.size || copy.isPending} onClick={() => copy.mutate([...selected])} className={`${btnPrimary} ml-auto`}>
            <BookmarkIcon className="w-4 h-4" /> Copy to My library{selected.size > 1 ? ` (${selected.size})` : ''}
          </button>
        </div>
      )}

      {/* items */}
      <ul className="space-y-1">
        {data.items.length === 0 && <li className="text-sm text-slate-400 py-3">{isOwner ? 'Nothing published yet — select items in My library and choose “Add to shared library”.' : 'This library is empty.'}</li>}
        {data.items.map((it) => (
          <li key={it.elementId} className={`rounded-lg border px-3 py-2 ${selected.has(it.elementId) ? 'border-amber-400 bg-amber-400/5' : 'border-transparent hover:bg-slate-700/40'}`}>
            <div className="flex items-center gap-2">
              {canBrowse && !isOwner && (
                <input type="checkbox" aria-label={`Select ${it.name}`} checked={selected.has(it.elementId)} onChange={() => toggle(it.elementId)} className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-amber-400 focus:ring-amber-400" />
              )}
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: TYPE_COLOR[it.type] }} />
              <button type="button" onClick={() => canBrowse && !isOwner ? toggle(it.elementId) : lib && onPreview([asElement(it, lib)])}
                className="font-semibold text-base text-white truncate text-left flex-1">{it.name}</button>
              {it.copy && (it.copy.stale
                ? <button type="button" onClick={() => copy.mutate([it.elementId])} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-amber-400 border border-amber-400/50 rounded px-1.5 py-0.5"><ArrowPathIcon className="w-3.5 h-3.5" /> Updated · re-copy</button>
                : <span className="text-xs font-bold uppercase tracking-wide text-emerald-300 border border-emerald-400/50 rounded px-1.5 py-0.5">In my library</span>)}
              {isOwner && (
                <button type="button" onClick={() => removeItem.mutate(it.elementId)} aria-label={`Remove ${it.name} from library`} className="text-slate-400 hover:text-red-300 p-1"><XMarkIcon className="w-4 h-4" /></button>
              )}
            </div>
            <div className="text-sm text-slate-400 mt-0.5 pl-[18px] tabular-nums">{itemMeta(it)}</div>
          </li>
        ))}
      </ul>

      {!canBrowse && data.items.length > 0 && (
        <p className="text-sm text-slate-400 flex items-start gap-1.5"><LockClosedIcon className="w-4 h-4 mt-0.5 shrink-0" /> Names and sizes are shown; the routes themselves unlock when you have access.</p>
      )}
      <RatingsSection id={id} data={data} onChanged={refresh} />

      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-slate-500 leading-relaxed">Shared by another skipper. Not an official chart product — verify against current charts and conditions before use.</p>
        {!isOwner && (
          <button type="button" onClick={() => setReporting(true)} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-300 shrink-0"><FlagIcon className="w-3.5 h-3.5" /> Report</button>
        )}
      </div>

      {reporting && <ReportModal libraryId={id} libraryName={lib.name} onClose={() => setReporting(false)} />}

      {confirmDelete && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-2">Delete “{lib.name}”?</h3>
            <p className="text-base text-slate-300 mb-5">Members lose access. Your own items stay in your library, and copies people already made are theirs to keep.</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmDelete(false)} className={btnGhost}>Cancel</button>
              <button type="button" onClick={() => del.mutate()} disabled={del.isPending} className={`${btn} bg-red-500 text-white hover:bg-red-400`}>{del.isPending ? 'Deleting…' : 'Delete library'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MembersPanel({ id, onChanged }: { id: string; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['nav-lib-members', id], queryFn: () => api.navLibMembers(id) });
  const decide = useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: 'approved' | 'declined' | 'revoked' }) => api.navLibDecide(id, userId, status),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['nav-lib-members', id] }); onChanged(); },
  });
  const rows = (data ?? []).filter((m: LibraryMember) => m.status !== 'declined' || true);
  const requested = rows.filter((m) => m.status === 'requested');
  const others = rows.filter((m) => m.status !== 'requested');
  const Row = ({ m }: { m: LibraryMember }) => (
    <li className="flex items-center gap-2 py-2 border-t border-slate-700/60">
      <div className="flex-1 min-w-0">
        <div className="text-base font-semibold text-white truncate">{m.userName ?? m.userEmail ?? 'Scout user'}</div>
        <div className="text-sm text-slate-400 truncate">{m.status === 'requested' ? `asked ${fmtAgo(m.requestedAt)}` : m.status}{m.message ? ` · “${m.message}”` : ''}</div>
      </div>
      {m.status === 'requested' && (<>
        <button type="button" onClick={() => decide.mutate({ userId: m.userId, status: 'approved' })} className={`${btn} bg-emerald-500/90 text-slate-900 hover:brightness-105`}><CheckIcon className="w-4 h-4" /> Approve</button>
        <button type="button" onClick={() => decide.mutate({ userId: m.userId, status: 'declined' })} className={btnGhost}>Decline</button>
      </>)}
      {m.status === 'approved' && <button type="button" onClick={() => decide.mutate({ userId: m.userId, status: 'revoked' })} className={btnDanger}>Remove</button>}
      {(m.status === 'declined' || m.status === 'revoked') && <button type="button" onClick={() => decide.mutate({ userId: m.userId, status: 'approved' })} className={btnGhost}>Approve</button>}
    </li>
  );
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3">
      <h3 className="text-base font-bold text-white">Requests & members</h3>
      {isLoading ? <div className="text-slate-400 text-sm py-2">Loading…</div> : rows.length === 0 ? <p className="text-sm text-slate-400 py-2">No one has asked to join yet.</p> : (<>
        {requested.length > 0 && <ul className="mt-1">{requested.map((m) => <Row key={m.id} m={m} />)}</ul>}
        {others.length > 0 && <ul className="mt-1">{others.map((m) => <Row key={m.id} m={m} />)}</ul>}
      </>)}
    </div>
  );
}

// ---------------- ratings ----------------

function RatingsSection({ id, data, onChanged }: { id: string; data: LibraryDetail; onChanged: () => void }) {
  const addToast = useToastStore((s) => s.addToast);
  const [stars, setStars] = useState<number>(data.myRating?.stars ?? 0);
  const [tags, setTags] = useState<Set<RatingTag>>(new Set((data.myRating?.tags ?? []) as RatingTag[]));
  const [text, setText] = useState(data.myRating?.text ?? '');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setStars(data.myRating?.stars ?? 0); setTags(new Set((data.myRating?.tags ?? []) as RatingTag[])); setText(data.myRating?.text ?? ''); }, [data.myRating]);

  const rate = useMutation({
    mutationFn: () => api.navLibRate(id, { stars, tags: [...tags], text: text.trim() || null }),
    onSuccess: () => { onChanged(); addToast({ type: 'success', title: data.myRating ? 'Rating updated' : 'Thanks for rating' }); },
    onError: (e) => setError(errMsg(e, 'Could not save your rating.')),
  });
  const unrate = useMutation({ mutationFn: () => api.navLibUnrate(id), onSuccess: onChanged });

  const { count, avg, tagCounts, recent } = data.ratings;
  const pct = (t: RatingTag) => (count ? Math.round((100 * (tagCounts[t] ?? 0)) / count) : 0);
  const isOwner = data.role === 'owner';

  return (
    <section aria-labelledby="lib-ratings-heading" className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 id="lib-ratings-heading" className="text-base font-bold text-white">Ratings</h3>
        <RatingLine avg={avg} count={count} />
      </div>
      {count > 0 && (
        <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm text-slate-300">
          {RATING_TAGS.map((t) => (
            <li key={t} className="flex items-center justify-between gap-2"><span>{RATING_TAG_LABEL[t]}</span><span className="tabular-nums text-slate-400">{pct(t)}%</span></li>
          ))}
        </ul>
      )}
      <p className="text-xs text-slate-500">Ratings come from skippers who have copied something from this library.</p>

      {!isOwner && (data.canRate ? (
        <div className="border-t border-slate-700/60 pt-3 space-y-2">
          <div className="flex items-center gap-2"><span className="text-sm font-semibold text-white">{data.myRating ? 'Your rating' : 'Rate this library'}</span><Stars value={stars} onChange={setStars} size="w-6 h-6" /></div>
          <div className="flex flex-wrap gap-1.5">
            {RATING_TAGS.map((t) => (
              <button key={t} type="button" aria-pressed={tags.has(t)} onClick={() => setTags((s) => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; })}
                className={`px-2.5 py-1 rounded-full border text-sm ${tags.has(t) ? 'border-amber-400 text-amber-300 bg-amber-400/10' : 'border-slate-600 text-slate-300'}`}>{RATING_TAG_LABEL[t]}</button>
            ))}
          </div>
          <input className={input} value={text} onChange={(e) => setText(e.target.value)} placeholder="One line for other skippers (optional)" maxLength={500} />
          {error && <p role="alert" className="text-sm font-medium text-red-300">{error}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={!stars || rate.isPending} onClick={() => rate.mutate()} className={btnPrimary}>{rate.isPending ? 'Saving…' : data.myRating ? 'Update rating' : 'Post rating'}</button>
            {data.myRating && <button type="button" onClick={() => unrate.mutate()} className={btnGhost}>Remove</button>}
          </div>
        </div>
      ) : data.canBrowse ? (
        <p className="text-sm text-slate-400 border-t border-slate-700/60 pt-3">Copy an item to your library first, then you can rate it.</p>
      ) : null)}

      {recent.length > 0 && (
        <ul className="border-t border-slate-700/60 pt-2 space-y-2">
          {recent.slice(0, 5).map((r) => (
            <li key={r.id} className="text-sm">
              <div className="flex items-center gap-2"><Stars value={r.stars} /><span className="font-semibold text-white">{r.userName ?? 'A skipper'}</span><span className="text-slate-500">{fmtAgo(r.updatedAt)}</span></div>
              {r.tags.length > 0 && <div className="text-slate-400">{r.tags.map((t) => RATING_TAG_LABEL[t as RatingTag] ?? t).join(' · ')}</div>}
              {r.text && <p className="text-slate-300 mt-0.5">“{r.text}”</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------- curator profile ----------------

function CuratorView({ userId, onBack, onOpen }: { userId: string; onBack: () => void; onOpen: (id: string) => void }) {
  const { data, isLoading } = useQuery({ queryKey: ['nav-lib-curator', userId], queryFn: () => api.navLibCurator(userId) });
  if (isLoading || !data) return <div className="text-slate-400 text-sm py-4">Loading…</div>;
  const t = data.totals;
  return (
    <div className="space-y-3">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-amber-300 hover:underline"><ArrowLeftIcon className="w-4 h-4" /> Back</button>
      <div>
        <h2 className="text-lg font-bold text-white">{data.name ?? 'A Scout user'}</h2>
        <div className="mt-1"><RatingLine avg={t.ratingAvg} count={t.ratingCount} /></div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-400 mt-1 tabular-nums">
          <span>{t.libraries} librar{t.libraries === 1 ? 'y' : 'ies'}</span><span>{t.items} items</span><span>{t.members} members</span>{t.copies > 0 && <span>copied {t.copies}×</span>}
        </div>
      </div>
      {data.libraries.length === 0 ? <p className="text-sm text-slate-400">No listed libraries.</p> : data.libraries.map((l) => (
        <button key={l.id} type="button" onClick={() => onOpen(l.id)} className="w-full text-left rounded-xl border border-slate-700 hover:border-amber-400 bg-slate-800/60 px-4 py-3">
          <div className="text-base font-bold text-white">{l.name}</div>
          {l.region && <div className="text-sm text-slate-300">{l.region}</div>}
          <div className="mt-1"><RatingLine avg={l.ratingAvg} count={l.ratingCount} /></div>
          <div className="text-sm text-slate-400 mt-1 tabular-nums">{l.itemCount} items · {l.memberCount} members{l.copyCount > 0 ? ` · copied ${l.copyCount}×` : ''}</div>
        </button>
      ))}
    </div>
  );
}

// ---------------- report ----------------

function ReportModal({ libraryId, libraryName, onClose }: { libraryId: string; libraryName: string; onClose: () => void }) {
  const addToast = useToastStore((s) => s.addToast);
  const [reason, setReason] = useState<ReportReason>('inaccurate');
  const [details, setDetails] = useState('');
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => api.navLibReport(libraryId, { reason, details: details.trim() || null }),
    onSuccess: () => { addToast({ type: 'success', title: 'Report sent', message: 'Thanks — the Vortex team will take a look.' }); onClose(); },
    onError: (e) => setError(errMsg(e, 'Could not send the report.')),
  });
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="report-title">
      <form className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md space-y-4" onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
        <h3 id="report-title" className="text-lg font-bold text-white">Report “{libraryName}”</h3>
        <p className="text-sm text-slate-300">Tell us what's wrong. The curator isn't told who reported it.</p>
        <fieldset className="space-y-1.5">
          <legend className="text-sm font-semibold text-slate-200 mb-1">Reason</legend>
          {REPORT_REASONS.map((r) => (
            <label key={r} className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer ${reason === r ? 'border-amber-400 bg-amber-400/5' : 'border-slate-700'}`}>
              <input type="radio" name="reason" value={r} checked={reason === r} onChange={() => setReason(r)} className="w-4 h-4 text-amber-400 focus:ring-amber-400" />
              <span className="text-base text-white">{REPORT_REASON_LABEL[r]}</span>
            </label>
          ))}
        </fieldset>
        <label className="block text-sm font-semibold text-slate-200">Details <span className="text-slate-400 font-normal">(optional)</span>
          <textarea className={`${input} mt-1`} rows={3} value={details} onChange={(e) => setDetails(e.target.value)} maxLength={1000} placeholder="Which item, and what you found" />
        </label>
        {error && <p role="alert" className="text-sm font-medium text-red-300 bg-red-900/30 border border-red-800/60 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="submit" disabled={send.isPending} className={`${btn} bg-red-500 text-white hover:bg-red-400`}>{send.isPending ? 'Sending…' : 'Send report'}</button>
        </div>
      </form>
    </div>
  );
}
